import { Typography, Divider, useTheme } from '@mui/material';
import { OrderDto, ProductDto } from 'desk-common';
import { formatCurrency } from '../../utils/functions/formatCurrency';
import { useMemo } from 'react';
import { useProductsMap } from '../../hooks/useProductsMap';
import { ColDef } from 'ag-grid-community';
import { AgGridReact } from 'ag-grid-react';
import {
  DEFAULT_CLIENT_COL_DEF,
  DEFAULT_LOCALIZATION,
} from '../../utils/agGrid';
import 'ag-grid-enterprise';

interface OrderSummaryTableProps {
  order: OrderDto;
  products: ProductDto[];
}

const DEFAULT_PRODUCT_DESC = 'PRODUCTO';
const DEFAULT_PROVIDER_NAME = 'SIN PROVEEDOR';
const TOTAL_LABEL = 'TOTAL';

interface OrderRow {
  providerName?: string;
  productDescription?: string;
  price?: number;
  qty?: number | string;
  subtotal?: number;
}

export const OrderSummaryTable = ({
  order,
  products,
}: OrderSummaryTableProps) => {
  const theme = useTheme();

  const productsMap = useProductsMap(products);

  const rowData = useMemo<OrderRow[]>(() => {
    return (
      order.items?.map((item) => {
        const product = productsMap.get(item.productId);
        const price = Number(item.unitPrice || 0);
        const qty = Number(item.quantity || 0);

        return {
          ...item,
          productDescription:
            product?.description?.toUpperCase() || DEFAULT_PRODUCT_DESC,
          providerName: product?.providerName || DEFAULT_PROVIDER_NAME,
          price,
          qty,
          subtotal: price * qty,
        };
      }) || []
    );
  }, [order.items, productsMap]);

  const columnDefs = useMemo<ColDef[]>(
    () => [
      {
        headerName: 'Proveedor',
        field: 'providerName',
        rowGroup: true,
        hide: true,
      },
      {
        headerName: 'Producto',
        field: 'productDescription',
        flex: 2,
        cellStyle: { fontWeight: 600 },
      },
      {
        headerName: 'Precio',
        field: 'price',
        width: 150,
        valueFormatter: (p) => formatCurrency(p.value),
      },
      {
        headerName: 'Cantidad',
        field: 'qty',
        width: 120,
        cellDataType: false,
        cellStyle: (params) => {
          if (params.node.rowPinned) {
            return { fontWeight: 'bold', textAlign: 'right' };
          }
          return null;
        },
      },
      {
        headerName: 'Subtotal',
        field: 'subtotal',
        width: 150,
        aggFunc: 'sum',
        valueFormatter: (p) => formatCurrency(p.value),
      },
    ],
    []
  );

  const autoGroupColumnDef = useMemo<ColDef>(
    () => ({
      headerName: 'Proveedor',
      hide: true,
      cellRendererParams: {
        suppressCount: false,
      },
    }),
    []
  );

  const pinnedBottomRowData = useMemo(
    () => [
      {
        qty: TOTAL_LABEL,
        subtotal: order.orderTotal,
      },
    ],
    [order.orderTotal]
  );

  return (
    <>
      <Divider sx={{ mb: 4 }} />
      <Typography variant="subtitle1" fontWeight={700} mb={3}>
        Productos
      </Typography>

      <div
        className="ag-theme-quartz"
        style={{ height: 'auto', width: '100%' }}
      >
        <AgGridReact<OrderRow>
          rowData={rowData}
          columnDefs={columnDefs}
          autoGroupColumnDef={autoGroupColumnDef}
          groupDefaultExpanded={-1}
          animateRows={true}
          suppressAggFuncInHeader={true}
          pinnedBottomRowData={pinnedBottomRowData}
          defaultColDef={DEFAULT_CLIENT_COL_DEF}
          localeText={DEFAULT_LOCALIZATION}
          domLayout="autoHeight"
          getRowStyle={(params) => {
            if (params.node.group) {
              return {
                backgroundColor: theme.palette.grey[50],
                fontWeight: '700',
                color: theme.palette.text.primary,
              };
            }
          }}
        />
      </div>
    </>
  );
};
